// ============================================================================
// EVIDENCE AUTO-LINK — background concept preparation for Packs (Phase 2C-2d)
//
// The concept library is the underground river. The specialist writes Pack
// criteria normally; this module silently prepares the Pack-owned evidence maps
// in the background (on Save / Publish) so OCR matching later searches against
// the published Pack — not the library directly. The specialist never has to
// configure evidence terms.
//
//   Specialist writes Pack criteria
//   → autoLinkPackEvidence fills EMPTY evidence maps from the concept library
//   → maps are normalized + saved on the Pack (Never-PHI)
//   → OCR searches those Pack-owned maps later
//   → GP confirms later
//   → Gate decides later
//
// SOURCE PRIORITY (top wins — auto-link never overwrites a higher tier):
//   1. Manual specialist edits        (conceptSource === 'MANUAL')
//   2. Existing Pack-local values      (reviewedBySpecialist === true, or any values)
//   3. Specialty library defaults      (SPECIALTY_LIBRARY)
//   4. Shared / global library defaults(GLOBAL_LIBRARY)
//   5. Deterministic drafting fallback (DRAFTED — red flags / attachments only)
//   6. Empty allowed                   (nothing useful/safe found → leave empty)
//
// PURE / Never-PHI: no Firebase, no Firestore, no PHI, no OCR, no external APIs.
// Reuses the static concept library (recognition) + the deterministic drafter
// (projection). Returns NEW objects; never mutates input. Produces only the
// 2C-1 authoring shapes + 2C-2c binding metadata — never the full concept object.
// ============================================================================

import {
  RECOGNITION_GROUPS,
  findConceptByText,
} from './evidenceConceptLibrary.js';
import {
  draftFieldEvidenceMap,
  draftOptionEvidence,
  draftRedFlagEvidenceMap,
  draftAttachmentEvidenceMap,
  evidenceMapHasValues,
} from './evidenceMapDrafting.js';

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

// ── status enum + labels (subtle UI chip) ────────────────────────────────────
export const EVIDENCE_STATUS = Object.freeze({
  READY:          'ready',
  NEEDS_REVIEW:   'needs_review',
  NOT_CONFIGURED: 'not_configured',
});

export const EVIDENCE_STATUS_LABELS = Object.freeze({
  ready:          'Evidence Assist ready',
  needs_review:   'Evidence Assist needs review',
  not_configured: 'Evidence Assist not configured',
});

// Confidence tiers (Never-PHI; consumed by the matcher 2C-3 later).
const HIGH_CONFIDENCE          = 0.7;   // ≥ this counts as "ready"
const SPECIALTY_CONFIDENCE     = 0.92;  // concept scoped to this specialty
const GLOBAL_CONFIDENCE        = 0.9;   // shared/global concept
const OTHER_SPECIALTY_CONFIDENCE = 0.85; // real concept scoped to another specialty
const DRAFTED_CONFIDENCE       = 0.4;   // label-derived fallback (needs review)

// Ordered FIELD recognition candidates (LAB then FINDING) — matches the drafter.
const FIELD_RECOGNITION_IDS = [...RECOGNITION_GROUPS.LAB, ...RECOGNITION_GROUPS.FINDING];

// ============================================================================
// STATUS — per evidence map (subtle chip; never specialist work)
// ============================================================================

/**
 * Classify an evidence map as ready / needs_review / not_configured.
 *  - ready          = manual, reviewed, high-confidence library link, or a
 *                     legacy 2C-1 map that already carries values.
 *  - needs_review   = drafted / low-confidence / ambiguous.
 *  - not_configured = no useful evidence map.
 * @param {Object|undefined|null} em
 * @returns {string} EVIDENCE_STATUS value
 */
export const evidenceMapStatus = (em) => {
  if (!isPlainObject(em)) return EVIDENCE_STATUS.NOT_CONFIGURED;
  if (em.conceptSource === 'MANUAL') return EVIDENCE_STATUS.READY;
  if (em.reviewedBySpecialist === true) return EVIDENCE_STATUS.READY;
  if (em.conceptSource === 'SPECIALTY_LIBRARY' || em.conceptSource === 'GLOBAL_LIBRARY') {
    return (typeof em.conceptConfidence === 'number' && em.conceptConfidence >= HIGH_CONFIDENCE)
      ? EVIDENCE_STATUS.READY
      : EVIDENCE_STATUS.NEEDS_REVIEW;
  }
  if (em.conceptSource === 'DRAFTED' || em.conceptSource === 'LOCAL_ONLY') {
    return EVIDENCE_STATUS.NEEDS_REVIEW;
  }
  // Legacy 2C-1 map with values but no conceptSource → treated as ready/manual.
  if (evidenceMapHasValues(em)) return EVIDENCE_STATUS.READY;
  return EVIDENCE_STATUS.NOT_CONFIGURED;
};

// ============================================================================
// LINKING — internal helpers (pure)
// ============================================================================

/**
 * An existing map is LOCKED (auto-link must never overwrite it) when it is
 * manual, reviewed, or already carries any values (incl. legacy 2C-1 maps).
 */
const isLockedMap = (em) => {
  if (!isPlainObject(em)) return false;
  if (em.conceptSource === 'MANUAL') return true;
  if (em.reviewedBySpecialist === true) return true;
  return evidenceMapHasValues(em);
};

/** Decide conceptSource + conceptConfidence from a recognized concept + specialty. */
const decideBinding = (concept, specialtyId) => {
  if (!concept) return { conceptSource: 'DRAFTED', conceptConfidence: DRAFTED_CONFIDENCE };
  if (concept.specialtyId && specialtyId && concept.specialtyId === specialtyId) {
    return { conceptSource: 'SPECIALTY_LIBRARY', conceptConfidence: SPECIALTY_CONFIDENCE };
  }
  if (concept.specialtyId == null) {
    return { conceptSource: 'GLOBAL_LIBRARY', conceptConfidence: GLOBAL_CONFIDENCE };
  }
  // Real library concept, scoped to a different specialty — still globally reusable.
  return { conceptSource: 'GLOBAL_LIBRARY', conceptConfidence: OTHER_SPECIALTY_CONFIDENCE };
};

/** Link a field: only when a REAL library concept is recognized THROUGH a
 * recognition group (LAB/FINDING). Recognition is group-driven (not the slug
 * fallback): draftFieldEvidenceMap falls back to conceptId = slugify(label) for
 * unrecognized fields, and a slug can coincide with a real DATA_ONLY conceptId
 * (e.g. a field literally labeled "Fever" slugifies to the data-only `fever`
 * concept). Binding on that slug would silently make data-only concepts
 * auto-link. Gating on findConceptByText keeps the original intent — a slug
 * fallback NEVER creates a link — so only LIVE (group-recognized) concepts bind. */
const linkField = (field, specialtyId) => {
  const searchText = `${field?.label || ''} ${field?.helpText || ''}`;
  const concept = findConceptByText(searchText, FIELD_RECOGNITION_IDS);
  if (!concept) return undefined;                       // not group-recognized → no link
  const draft = draftFieldEvidenceMap(field);
  const { conceptSource, conceptConfidence } = decideBinding(concept, specialtyId);
  return { ...draft, conceptSource, conceptConfidence, reviewedBySpecialist: false };
};

/** Link one option: only when a recognized finding concept matches its label. */
const linkOption = (optionValue, specialtyId) => {
  const concept = findConceptByText(String(optionValue || ''), RECOGNITION_GROUPS.FINDING);
  if (!concept) return undefined;
  const draft = draftOptionEvidence(optionValue);       // aliases/phrases (no conceptId)
  const { conceptSource, conceptConfidence } = decideBinding(concept, specialtyId);
  return { ...draft, conceptId: concept.conceptId, conceptSource, conceptConfidence, reviewedBySpecialist: false };
};

/** Link a red flag: recognized concept → high confidence; else label-derived DRAFTED. */
const linkRedFlag = (flag, specialtyId) => {
  const concept = findConceptByText(`${flag?.label || ''} ${flag?.description || ''}`, RECOGNITION_GROUPS.RED_FLAG);
  const draft = draftRedFlagEvidenceMap(flag);          // forces positive_only + requireExplicit
  if (concept) {
    const { conceptSource, conceptConfidence } = decideBinding(concept, specialtyId);
    return { ...draft, conceptId: concept.conceptId, conceptSource, conceptConfidence, reviewedBySpecialist: false };
  }
  // No concept: keep the label-derived map only when it has a positive phrase
  // (useful + safe — red flags surface only on explicit positive evidence).
  if (Array.isArray(draft.positivePhrases) && draft.positivePhrases.length) {
    return { ...draft, conceptSource: 'DRAFTED', conceptConfidence: DRAFTED_CONFIDENCE, reviewedBySpecialist: false };
  }
  return undefined;
};

/** Link an attachment: recognized concept → high confidence; else keep only if a doc-type hint was inferred. */
const linkAttachment = (att, specialtyId) => {
  const concept = findConceptByText(`${att?.label || ''} ${att?.description || ''}`, RECOGNITION_GROUPS.LAB);
  const draft = draftAttachmentEvidenceMap(att);        // documentTypeHints/aliases/phrases
  if (concept) {
    const { conceptSource, conceptConfidence } = decideBinding(concept, specialtyId);
    return { ...draft, conceptId: concept.conceptId, conceptSource, conceptConfidence, reviewedBySpecialist: false };
  }
  if (Array.isArray(draft.documentTypeHints) && draft.documentTypeHints.length) {
    return { ...draft, conceptSource: 'DRAFTED', conceptConfidence: DRAFTED_CONFIDENCE, reviewedBySpecialist: false };
  }
  return undefined;
};

// ============================================================================
// AUTO-LINK — the public entry point
// ============================================================================

/**
 * Return a NEW Pack with EMPTY evidence maps filled from the concept library in
 * the background. Manual / reviewed / existing / legacy maps are preserved
 * untouched. Pure + immutable: never mutates `pack`.
 *
 * @param {Object} pack         Pack-like object (fields[], redFlags[], attachmentRequirements[])
 * @param {string|null} specialtyId  Pack specialty (defaults to pack.specialtyId)
 * @returns {Object} new Pack with prepared evidence maps
 */
export const autoLinkPackEvidence = (pack, specialtyId = null) => {
  if (!isPlainObject(pack)) return pack;
  const spec = specialtyId || pack.specialtyId || null;

  const fields = Array.isArray(pack.fields) ? pack.fields.map((field) => {
    if (!isPlainObject(field)) return field;
    let next = field;

    // Field-level evidence map.
    if (!isLockedMap(field.evidenceMap)) {
      const linked = linkField(field, spec);
      if (linked) next = { ...next, evidenceMap: linked };
    }

    // Option-level evidence maps (only option-type fields have options).
    const options = Array.isArray(field.options) ? field.options : [];
    if (options.length) {
      const existing = isPlainObject(field.optionEvidenceMap) ? field.optionEvidenceMap : {};
      let oem = existing;
      let changed = false;
      for (const opt of options) {
        const value = isPlainObject(opt) ? opt.value : opt;
        if (typeof value !== 'string' || !value.trim()) continue;
        if (isLockedMap(existing[value])) continue;       // preserve manual/existing
        const linked = linkOption(value, spec);
        if (linked) {
          if (!changed) { oem = { ...existing }; changed = true; }
          oem[value] = linked;
        }
      }
      if (changed) next = { ...next, optionEvidenceMap: oem };
    }

    return next;
  }) : pack.fields;

  const redFlags = Array.isArray(pack.redFlags) ? pack.redFlags.map((flag) => {
    if (!isPlainObject(flag)) return flag;
    if (isLockedMap(flag.evidenceMap)) return flag;
    const linked = linkRedFlag(flag, spec);
    return linked ? { ...flag, evidenceMap: linked } : flag;
  }) : pack.redFlags;

  const attachmentRequirements = Array.isArray(pack.attachmentRequirements) ? pack.attachmentRequirements.map((att) => {
    if (!isPlainObject(att)) return att;
    if (isLockedMap(att.evidenceMap)) return att;
    const linked = linkAttachment(att, spec);
    return linked ? { ...att, evidenceMap: linked } : att;
  }) : pack.attachmentRequirements;

  return { ...pack, fields, redFlags, attachmentRequirements };
};

/**
 * Summarize evidence status across every binding point in a Pack. Pure read for
 * an optional subtle header line. Never required for the workflow.
 * @param {Object} pack
 * @returns {{ ready: number, needs_review: number, not_configured: number, total: number }}
 */
export const summarizePackEvidenceStatus = (pack) => {
  const counts = { ready: 0, needs_review: 0, not_configured: 0, total: 0 };
  if (!isPlainObject(pack)) return counts;
  const tally = (em) => { counts.total += 1; counts[evidenceMapStatus(em)] += 1; };

  (Array.isArray(pack.fields) ? pack.fields : []).forEach((field) => {
    if (!isPlainObject(field)) return;
    tally(field.evidenceMap);
    const oem = isPlainObject(field.optionEvidenceMap) ? field.optionEvidenceMap : {};
    Object.values(oem).forEach(tally);
  });
  (Array.isArray(pack.redFlags) ? pack.redFlags : []).forEach((rf) => {
    if (isPlainObject(rf)) tally(rf.evidenceMap);
  });
  (Array.isArray(pack.attachmentRequirements) ? pack.attachmentRequirements : []).forEach((att) => {
    if (isPlainObject(att)) tally(att.evidenceMap);
  });
  return counts;
};
